import { z } from "zod";

import { isAdwSchema, simpleDateSchema } from "@/src/models/common-schema";
import { ImagingResultsDocumentType } from "@/src/models/constants/document-types";
import { ImagingType } from "@/src/models/constants/imaging-types";
import {
  ORDERING_PROVIDER_PROMPT,
  BODY_PART_PROMPT,
  BODY_PARTS_PROMPT,
  IMAGING_COMPANY_NAME_PROMPT,
  BODY_PART_LATERALITY_PROMPT,
  ABNORMAL_RESULT_PROMPT,
  FOLLOW_UP_REQUIRED_PROMPT,
  IMPRESSION_PROMPT,
  STUDY_TYPE_PROMPT,
} from "@/src/models/constants/prompt";
import { DatabaseDefaults } from "@/src/models/database-defaults";
import { DocumentDateSchema } from "@/src/models/documents/document-dates";
import { ExtractedDocumentFields } from "@/src/models/documents/extracted-document-fields";
import { locationSchema } from "@/src/models/location";
import { Patient, patientSchema } from "@/src/models/patient";
import { practitionerSchema } from "@/src/models/practitioner";

// Can't move to blocks.yaml: a description on the parent `imagingStudies` rule is silently dropped
// once any child rule is configured. Keep example values inside the shared enums — demonstrating a
// tenant's narrowed vocabulary would steer other tenants toward a value their `options` reject.
const IMAGING_STUDIES_PROMPT = `
Break the document down into one entry per distinct imaging study, so each modality stays paired with
the body part and laterality it was actually performed on.

## Output Format
Return an array of objects, each with imagingType, bodyPart, and bodyPartLaterality. Return null when
no imaging study can be identified.

## Rules
- One entry per modality/body-part pair. A single report covering two body parts with the same modality
  yields two entries; two modalities on the same body part also yield two entries.
- Never spread one study's body part onto another study's modality. Read each report header/impression
  and attribute the body part to the modality that study used.
- bodyPartLaterality is per entry. Two studies on different sides each keep their own side, and two
  studies that happen to cover the same side still each carry it.
- Keep each entry's imagingType and bodyPart in the same vocabulary used by the imagingTypes and
  bodyParts fields, and make sure every modality listed in imagingTypes appears in at least one entry.
- Do not emit duplicate entries for repeated copies or continuation pages of the same study.

## Examples

Text: "MRI RIGHT SHOULDER ... XR LEFT KNEE" →
[{ imagingType: "MRI", bodyPart: "Shoulder", bodyPartLaterality: "Right" },
 { imagingType: "X-Rays", bodyPart: "Knee", bodyPartLaterality: "Left" }]

Text: "MRI right knee without contrast" →
[{ imagingType: "MRI", bodyPart: "Knee", bodyPartLaterality: "Right" }]

`;

// `bodyPart`/`bodyPartLaterality` are strings, not the shared enums: tenants narrow them via `options`.
export const imagingStudySchema = z.object({
  imagingType: z.nativeEnum(ImagingType).nullable(),
  bodyPart: z.string().nullable(),
  bodyPartLaterality: z.string().nullable(),
});

export type ImagingStudy = z.infer<typeof imagingStudySchema>;

export const imagingStudiesSchema = z.array(imagingStudySchema).nullable().describe(IMAGING_STUDIES_PROMPT);

const NON_RENDERABLE_LATERALITY = new Set(["other", "unspecified"]);

function readStudyString(record: Record<string, unknown>, key: string): string | null {
  const value = record[key];

  return typeof value === "string" && value.trim().length > 0 ? value.trim() : null;
}

// Structural rather than a Zod parse: one unrecognised entry must not blank out the whole title.
export function toImagingStudies(value: unknown): ImagingStudy[] {
  if (!Array.isArray(value)) return [];

  return value
    .filter((item): item is Record<string, unknown> => typeof item === "object" && item !== null)
    .map((item) => ({
      imagingType: readStudyString(item, "imagingType") as ImagingStudy["imagingType"],
      bodyPart: readStudyString(item, "bodyPart"),
      bodyPartLaterality: readStudyString(item, "bodyPartLaterality"),
    }));
}

function formatImagingStudy(study: ImagingStudy): string {
  const canonical = study.bodyPartLaterality?.toLowerCase().replace(/[\s._-]/g, "");
  const laterality = canonical && !NON_RENDERABLE_LATERALITY.has(canonical) ? study.bodyPartLaterality : null;

  return [laterality, study.bodyPart, study.imagingType]
    .filter((part): part is string => typeof part === "string")
    .join(" ");
}

function readModalityList(value: unknown): string[] {
  return (Array.isArray(value) ? value : [value])
    .filter((item): item is string => typeof item === "string" && item.trim().length > 0)
    .map((item) => item.trim());
}

/**
 * e.g. "Bilat Shoulder MRI, Bilat Leg X-Rays". Empty string when nothing renders.
 *
 * `flatModalities` (the `imagingTypes` the same extraction produced) is reconciled in rather than
 * trusted to match: a partially populated `imagingStudies` is still schema-valid, so a document that
 * bound the MRI to a body part but left a separately extracted CT unpaired would otherwise drop the
 * CT from the label entirely. Unpaired modalities render bare, after the studies that did bind one.
 *
 * Reconciliation only applies once at least one study renders. When none do, this returns "" so the
 * caller's all-or-nothing flat-field fallback — which also contributes body parts and laterality —
 * takes over unchanged.
 */
export function formatImagingStudies(value: unknown, flatModalities?: unknown): string {
  const studies = toImagingStudies(value);
  const rendered = studies.map(formatImagingStudy).filter((entry) => entry.length > 0);

  if (rendered.length === 0) return "";

  const paired = new Set(
    studies
      .map((study) => study.imagingType)
      .filter((type): type is NonNullable<ImagingStudy["imagingType"]> => type !== null)
      .map((type) => type.toLowerCase())
  );
  const unpaired = readModalityList(flatModalities).filter((modality) => !paired.has(modality.toLowerCase()));

  return [...new Set([...rendered, ...unpaired])].join(", ");
}

export const imagingResultsTypeDisplay: Record<ImagingResultsDocumentType, string> = {
  [ImagingResultsDocumentType.BARIUM_SWALLOW]: "Modified Barium Swallow",
  [ImagingResultsDocumentType.CLINICAL]: "Clinical Imaging",
  [ImagingResultsDocumentType.COLONOSCOPY]: "Colonoscopy",
  [ImagingResultsDocumentType.CT]: "CT",
  [ImagingResultsDocumentType.DEXA]: "DEXA",
  [ImagingResultsDocumentType.ECHO]: "Echo",
  [ImagingResultsDocumentType.EEG]: "EEG",
  [ImagingResultsDocumentType.EKG]: "EKG",
  [ImagingResultsDocumentType.EMG]: "EMG",
  [ImagingResultsDocumentType.ENG]: "ENG/VNG",
  [ImagingResultsDocumentType.ESOPHAGRAM]: "Esophagram",
  [ImagingResultsDocumentType.HOLTER_MONITOR]: "Holter Monitor",
  [ImagingResultsDocumentType.MAMMOGRAM]: "Mammogram",
  [ImagingResultsDocumentType.MULTI]: "Multiple Imaging Results",
  [ImagingResultsDocumentType.MRI]: "MRI",
  [ImagingResultsDocumentType.NM]: "Nuclear",
  [ImagingResultsDocumentType.OTHER]: "Imaging Results - Other",
  [ImagingResultsDocumentType.OTHER_CARDIAC]: "Other Cardiac",
  [ImagingResultsDocumentType.PET]: "PET",
  [ImagingResultsDocumentType.PULMONARY_FUNCTION]: "Pulmonary Function/Spiro",
  [ImagingResultsDocumentType.RETINAL]: "Retinal",
  [ImagingResultsDocumentType.STRESS_TEST]: "Stress Test",
  [ImagingResultsDocumentType.STRESS_TEST_EXERCISE]: "Stress Test (exercise)",
  [ImagingResultsDocumentType.STRESS_TEST_NM]: "Stress Test (nm)",
  [ImagingResultsDocumentType.ULTRASOUND]: "Ultrasound",
  [ImagingResultsDocumentType.X_RAY]: "X-Ray",
  [ImagingResultsDocumentType.NEEDS_SIGNATURE]: "Imaging Orders - Signature Needed",
  [ImagingResultsDocumentType.NEEDS_CLARIFICATION]: "Imaging Orders - Clarification Needed",
  [ImagingResultsDocumentType.IMAGING_RESULTS]: "Imaging Results",
  [ImagingResultsDocumentType.ENDOSCOPY]: "Endoscopy",
  [ImagingResultsDocumentType.MONITOR_NOTIFICATION]: "Monitor Notification",
  [ImagingResultsDocumentType.CARDIAC_LEG_IMAGING]: "Cardiac Leg Imaging",
};

export const imagingResultsTypeSchema = z.nativeEnum(ImagingResultsDocumentType);

const baseImagingResultsFields = {
  patient: patientSchema,
  dateOfImagingResults: simpleDateSchema
    .nullable()
    .describe(
      "Date the imaging results were collected. Look for content in the document like: " +
        "'Exam Date', 'Report Date', ..." +
        "There should be a clear indication of what the date represents from the context of the document. " +
        "Format as YYYY-MM-DD."
    ),
  receivingLocation: locationSchema.nullable(),
  receivingProvider: practitionerSchema.nullable(),
  abnormal: z.boolean().nullable().describe(ABNORMAL_RESULT_PROMPT),
  followUpRequired: z.boolean().nullable().describe(FOLLOW_UP_REQUIRED_PROMPT),
  impression: z.string().nullable().describe(IMPRESSION_PROMPT),
  studyType: z.string().nullable().describe(STUDY_TYPE_PROMPT),
};
const extendedImagingResultsSchema = z.object({
  isAdw: isAdwSchema,
  ...DocumentDateSchema.shape,
  orderingProvider: practitionerSchema.nullable().describe(ORDERING_PROVIDER_PROMPT),
  companyName: z.string().nullable().describe(IMAGING_COMPANY_NAME_PROMPT),
  imagingType: z.nativeEnum(ImagingType).nullable(),
  imagingTypes: z.array(z.nativeEnum(ImagingType)).nullable(),
  bodyPart: z.string().nullable().describe(BODY_PART_PROMPT),
  bodyParts: z.array(z.string()).nullable().describe(BODY_PARTS_PROMPT),
  bodyPartLaterality: z.string().nullable().describe(BODY_PART_LATERALITY_PROMPT),
  // Naming only — folder routing reads the flat `imagingTypes`.
  imagingStudies: imagingStudiesSchema,
  needsSignature: z.boolean().nullable(),
  signatureRequired: z.boolean().nullable(),
  needsClarification: z.boolean().nullable(),
  procedure: z.string().nullable(),
  location: locationSchema.nullable(),
  significantFindings: z.boolean().nullable(),
  isOverread: z
    .boolean()
    .nullable()
    .describe(
      "True when this report is a second opinion — a subsequent expert review and interpretation of a study previously read by another radiologist or provider — rather than the original/primary read."
    ),
});
const imagingResultsCustomFields: Record<ImagingResultsDocumentType, Record<string, z.ZodTypeAny>> = {
  [ImagingResultsDocumentType.IMAGING_RESULTS]: extendedImagingResultsSchema.shape,
  [ImagingResultsDocumentType.BARIUM_SWALLOW]: extendedImagingResultsSchema.pick({
    dateOfEvent: true,
    orderingProvider: true,
  }).shape,
  [ImagingResultsDocumentType.CLINICAL]: extendedImagingResultsSchema.pick({
    dateOfEvent: true,
    bodyPart: true,
    orderingProvider: true,
  }).shape,
  [ImagingResultsDocumentType.COLONOSCOPY]: extendedImagingResultsSchema.pick({
    dateOfEvent: true,
    orderingProvider: true,
  }).shape,
  [ImagingResultsDocumentType.CT]: extendedImagingResultsSchema.pick({
    isAdw: true,
    dateOfEvent: true,
    bodyPart: true,
    bodyPartLaterality: true,
    orderingProvider: true,
    procedure: true,
    signatureRequired: true,
    isOverread: true,
  }).shape,
  [ImagingResultsDocumentType.DEXA]: extendedImagingResultsSchema.pick({
    isAdw: true,
    dateOfEvent: true,
    orderingProvider: true,
  }).shape,
  [ImagingResultsDocumentType.ECHO]: extendedImagingResultsSchema.pick({
    isAdw: true,
    dateOfEvent: true,
    imagingType: true,
    orderingProvider: true,
  }).shape,
  [ImagingResultsDocumentType.EEG]: extendedImagingResultsSchema.pick({
    isAdw: true,
    dateOfEvent: true,
    orderingProvider: true,
  }).shape,
  [ImagingResultsDocumentType.EMG]: extendedImagingResultsSchema.pick({
    isAdw: true,
    dateOfEvent: true,
    orderingProvider: true,
    bodyPart: true,
    bodyPartLaterality: true,
  }).shape,
  [ImagingResultsDocumentType.ENG]: extendedImagingResultsSchema.pick({ dateOfEvent: true, orderingProvider: true })
    .shape,
  [ImagingResultsDocumentType.EKG]: extendedImagingResultsSchema.pick({
    isAdw: true,
    dateOfEvent: true,
    imagingType: true,
    orderingProvider: true,
    companyName: true,
    significantFindings: true,
  }).shape,
  [ImagingResultsDocumentType.ESOPHAGRAM]: extendedImagingResultsSchema.pick({
    isAdw: true,
    dateOfEvent: true,
    orderingProvider: true,
  }).shape,
  [ImagingResultsDocumentType.HOLTER_MONITOR]: extendedImagingResultsSchema.pick({
    isAdw: true,
    dateOfEvent: true,
    orderingProvider: true,
  }).shape,
  [ImagingResultsDocumentType.MAMMOGRAM]: extendedImagingResultsSchema.pick({
    isAdw: true,
    dateOfEvent: true,
    orderingProvider: true,
  }).shape,
  [ImagingResultsDocumentType.MULTI]: extendedImagingResultsSchema.pick({
    dateOfEvent: true,
    orderingProvider: true,
  }).shape,
  [ImagingResultsDocumentType.MRI]: extendedImagingResultsSchema.pick({
    isAdw: true,
    dateOfEvent: true,
    orderingProvider: true,
    bodyPart: true,
    bodyPartLaterality: true,
    signatureRequired: true,
  }).shape,
  [ImagingResultsDocumentType.NM]: extendedImagingResultsSchema.pick({
    dateOfEvent: true,
    orderingProvider: true,
  }).shape,
  [ImagingResultsDocumentType.OTHER]: extendedImagingResultsSchema.shape,
  [ImagingResultsDocumentType.OTHER_CARDIAC]: extendedImagingResultsSchema.pick({
    isAdw: true,
    dateOfEvent: true,
    procedure: true,
  }).shape,
  [ImagingResultsDocumentType.PET]: extendedImagingResultsSchema.pick({
    dateOfEvent: true,
    imagingType: true,
    dateReceived: true,
    bodyPart: true,
    orderingProvider: true,
    procedure: true,
  }).shape,
  [ImagingResultsDocumentType.PULMONARY_FUNCTION]: extendedImagingResultsSchema.pick({
    isAdw: true,
    dateOfEvent: true,
    orderingProvider: true,
  }).shape,
  [ImagingResultsDocumentType.RETINAL]: extendedImagingResultsSchema.pick({
    isAdw: true,
    dateOfEvent: true,
    orderingProvider: true,
  }).shape,
  [ImagingResultsDocumentType.STRESS_TEST]: extendedImagingResultsSchema.pick({
    isAdw: true,
    dateOfEvent: true,
    orderingProvider: true,
  }).shape,
  [ImagingResultsDocumentType.STRESS_TEST_EXERCISE]: extendedImagingResultsSchema.pick({
    dateOfEvent: true,
    orderingProvider: true,
  }).shape,
  [ImagingResultsDocumentType.STRESS_TEST_NM]: extendedImagingResultsSchema.pick({
    dateOfEvent: true,
    orderingProvider: true,
  }).shape,
  [ImagingResultsDocumentType.ULTRASOUND]: extendedImagingResultsSchema.pick({
    isAdw: true,
    dateOfEvent: true,
    bodyPart: true,
    bodyPartLaterality: true,
    companyName: true,
    orderingProvider: true,
    signatureRequired: true,
    imagingType: true,
    significantFindings: true,
  }).shape,
  [ImagingResultsDocumentType.X_RAY]: extendedImagingResultsSchema.pick({
    isAdw: true,
    dateOfEvent: true,
    bodyPart: true,
    companyName: true,
    orderingProvider: true,
    significantFindings: true,
  }).shape,
  [ImagingResultsDocumentType.NEEDS_CLARIFICATION]: extendedImagingResultsSchema.pick({
    dateReceived: true,
    bodyPart: true,
    imagingType: true,
  }).shape,
  [ImagingResultsDocumentType.NEEDS_SIGNATURE]: extendedImagingResultsSchema.pick({
    dateReceived: true,
    bodyPart: true,
    imagingType: true,
  }).shape,
  [ImagingResultsDocumentType.ENDOSCOPY]: extendedImagingResultsSchema.shape,
  [ImagingResultsDocumentType.MONITOR_NOTIFICATION]: extendedImagingResultsSchema.shape,
  [ImagingResultsDocumentType.CARDIAC_LEG_IMAGING]: extendedImagingResultsSchema.shape,
};

const makeImagingResultsSchema = (type: ImagingResultsDocumentType, customFields: Record<string, z.ZodTypeAny>) =>
  z.object({
    type: z.literal(type),
    ...baseImagingResultsFields,
    ...customFields,
  });

export const imagingResultsSchemas: Record<ImagingResultsDocumentType, ReturnType<typeof makeImagingResultsSchema>> = (
  Object.values(ImagingResultsDocumentType) as ImagingResultsDocumentType[]
).reduce(
  (acc, type) => {
    acc[type] = makeImagingResultsSchema(type, imagingResultsCustomFields[type]);

    return acc;
  },
  {} as Record<ImagingResultsDocumentType, ReturnType<typeof makeImagingResultsSchema>>
);

const imagingResultsSchemasArray = (Object.values(ImagingResultsDocumentType) as ImagingResultsDocumentType[]).map(
  (type) => imagingResultsSchemas[type]
) as [ReturnType<typeof makeImagingResultsSchema>, ...ReturnType<typeof makeImagingResultsSchema>[]];

export const anyImagingResultsSchema = z.discriminatedUnion("type", imagingResultsSchemasArray);

type ImagingResultsPersistedFields = {
  patient: Patient | null;
};

/** See {@link ExtractedDocumentFields} for why the fields are reconstructed rather than inferred. */
export type ImagingResults = Omit<
  ExtractedDocumentFields<
    ImagingResultsDocumentType,
    typeof extendedImagingResultsSchema,
    typeof baseImagingResultsFields
  >,
  keyof ImagingResultsPersistedFields
> &
  ImagingResultsPersistedFields &
  DatabaseDefaults;

const now = new Date().toISOString();

export const imagingResultsShape: ImagingResults = {
  id: "",
  type: ImagingResultsDocumentType.OTHER,
  patient: null,
  dateOfImagingResults: null,
  receivingLocation: null,
  receivingProvider: null,
  orderingProvider: null,
  companyName: null,
  bodyPart: null,
  bodyParts: null,
  bodyPartLaterality: null,
  imagingType: null,
  imagingTypes: null,
  imagingStudies: null,
  studyType: null,
  procedure: null,
  isOverread: null,
  abnormal: null,
  followUpRequired: null,
  impression: null,
  dateOfEvent: null,
  dateReceived: null,
  createdAt: now,
  updatedAt: now,
  deletedAt: null,
  groupId: "",
};
